import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  ISystemHelp,
  fetchSystemHelpApiCall,
  getSystemHelpFields,
  handleActiveInactiveFaqs,
  handleDeleteSystemHelp,
} from "./SystemHelpController";
import usePagination from "../../hooks/UsePaginationHook";
import { PER_PAGE_ROWS } from "../../config/AppConfig";
import { IPagination } from "../../data/AppInterface";
import { editIcon, eyeIcon, restoreIcon } from "../../assets/icons/SvgIconList";
import ICSweetAlertModal from "../../core-component/ICSweetAlertModal";
import { ORDER_BY } from "../../utils/AppConstants";
import AdvanceSearch from "../../components/page-layouts/AdvanceSearch";
import ICTable from "../../core-component/ICTable";
import SystemHelpForm from "./SystemHelpForm";

const SystemHelpView = () => {
  const { t } = UseTranslationHook();
  let timer: NodeJS.Timeout;
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [readOnlyForm, setReadOnlyForm] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [systemHelpList, setSystemHelpList] = useState<ISystemHelp[]>([]);
  const [systemHelpToEdit, setSystemHelpToEdit] = useState<ISystemHelp>();
  const [deleteAll, setDeleteAll] = useState(false);
  const { pagination, setPagination } = usePagination({
    pagination: {
      perPageRows: Number(PER_PAGE_ROWS),
      currentPage: 1,
      sortBy: "topic",
      orderBy: "ASC",
    },
  });
  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnlyForm(false);
    if (systemHelpToEdit) {
      setSystemHelpToEdit(undefined);
    } else {
      setEnterpriseSearch("");
    }
    if (status) {
      fetchSystemHelp(pagination);
    }
  };
  const fetchSystemHelp = (payloadPagination: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await fetchSystemHelpApiCall(
        setSystemHelpList,
        setPagination,
        payloadPagination,
        {
          enterpriseSearch,
          deleteAll,
        }
      );
      setIsLoading(false);
    }, 500);
  };
  useEffect(() => {
    fetchSystemHelp(pagination);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [enterpriseSearch, deleteAll]);
  const handleSystemHelpStatus = async (values: ISystemHelp) => {
    var isStatusChange = await handleActiveInactiveFaqs(
      values.id,
      values.is_active === "1" ? "0" : "1"
    );
    if (isStatusChange) {
      const updatedSystemHelpList = systemHelpList.map((faqs) => {
        if (faqs.id === values.id) {
          return {
            ...faqs,
            is_active: values.is_active === "1" ? "0" : "1",
          };
        }
        return faqs;
      });

      setSystemHelpList(updatedSystemHelpList);
    }
  };
  const handleDeleteCfm = async (id: number) => {
    const data = await handleDeleteSystemHelp(id);
    if (data) {
      fetchSystemHelp(pagination);
    }
  };
  const customActionView = (item: ISystemHelp) => {
    return (
      <div>
        {item.deleted === "1" ? (
          <div className="flex item-center justify-center ml-2">
            <div
              className="mr-2 action-view"
              title={t("restore")}
              onClick={() => handleDeleteCfm(item.id)}
            >
              {restoreIcon}
            </div>
          </div>
        ) : (
          <div className="flex item-center justify-center ml-2">
            <div
              className="mr-2 action-view"
              title={t("view_tooltip_icon")}
              onClick={() => {
                setShowForm(true);
                setSystemHelpToEdit(item);
                setReadOnlyForm(true);
              }}
            >
              {eyeIcon}
            </div>
            <div
              className="mr-2 action-view "
              title={t("edit_tooltip_icon")}
              onClick={() => {
                setShowForm(true);
                setSystemHelpToEdit(item);
              }}
            >
              {editIcon}
            </div>
            <div className="action-view " title={t("delete_tooltip_icon")}>
              <ICSweetAlertModal onConfirm={handleDeleteCfm} itemId={item.id} />
            </div>
          </div>
        )}
      </div>
    );
  };
  const allSystemHelpList = systemHelpList.map((val) => ({
    ...val,
    IsDeleted: val.deleted === "1" ? true : false,
  }));
  return (
    <div className="w-full overflow-auto scrollbar-remove">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        handleDeleteCheckBox={(ans) => {
          setDeleteAll(ans);
        }}
        filterCheckBox={deleteAll}
        refreshTable={() => fetchSystemHelp(pagination)}
      />
      <ICTable<ISystemHelp>
        isLoading={isLoading}
        totalFlex="grid grid-cols-15"
        fields={getSystemHelpFields(
          handleSystemHelpStatus,
          customActionView,
          t
        )}
        data={allSystemHelpList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchSystemHelp({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchSystemHelp({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchSystemHelp({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {showForm && (
        <SystemHelpForm
          handleClose={handleCloseForm}
          systemHelpToEdit={systemHelpToEdit}
          readOnly={readOnlyForm}
        />
      )}
    </div>
  );
};
export default SystemHelpView;
